import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import { Save, KeyRound, Mail, History, User as UserIcon } from "lucide-react";

const Input = (props) => (
  <input {...props}
    className={
      "w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm " +
      "focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none " + (props.className || "")
    } />
);
const Field = ({ label, children }) => (
  <div><label className="text-label block mb-1.5">{label}</label>{children}</div>
);

export default function Profile() {
  const { user, reload } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "profile";
  const [tab, setTab] = useState(initialTab);
  const [profileForm, setProfileForm] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (user) setProfileForm({
      full_name: user.full_name || "",
      mobile: user.mobile || "",
      designation: user.designation || "",
      photo_url: user.photo_url || "",
      email: user.email || "",
    });
  }, [user]);

  useEffect(() => {
    if (tab === "history") {
      api.get("/auth/login-history").then(({ data }) => setHistory(data)).catch(() => {});
    }
    setParams((p) => { p.set("tab", tab); return p; }, { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  if (!user) return null;

const uploadPhoto = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      "/auth/upload-profile-photo",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
	console.log("UPLOAD RESPONSE", response.data);

	setProfileForm((f) => ({
  	...f,
  	photo_url: response.data.url,
	}));

  } catch (err) {
    console.error(err);
    alert("Photo upload failed");
  }
};

  const saveProfile = async () => {
    try {
      await api.patch("/auth/profile", {
        full_name: profileForm.full_name,
        mobile: profileForm.mobile,
        designation: profileForm.designation,
        photo_url: profileForm.photo_url || null,
      });
      toast.success("Profile updated");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePwd = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    if (pwd.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEmail = async () => {
    try {
      await api.post("/auth/change-email", emailForm);
      toast.success("Email changed. Use the new email on next login.");
      setEmailForm({ new_email: "", password: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const initials = (user.full_name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-3xl" data-testid="profile-page">
      <header className="flex items-center gap-4">
        
{user.photo_url ? (
  <img
  src={
    user.photo_url?.startsWith("http")
      ? user.photo_url
      : `${import.meta.env.VITE_API_URL}${user.photo_url}`
  }
  alt=""
  className="w-14 h-14 rounded-sm object-cover border border-border"
/>
) : (


          <div className="w-14 h-14 rounded-sm flex items-center justify-center text-lg font-semibold text-white"
            style={{ backgroundColor: "var(--theme-color)" }}>{initials}</div>
        )}
        <div>
          <p className="text-label">Account</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold">{user.full_name}</h1>
          <p className="text-sm text-muted-foreground">
            {user.email || user.username} · {ROLE_LABEL[user.role]}
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="profile" data-testid="ptab-profile"><UserIcon className="w-3.5 h-3.5 mr-1.5" />Profile</TabsTrigger>
          <TabsTrigger value="password" data-testid="ptab-password"><KeyRound className="w-3.5 h-3.5 mr-1.5" />Password</TabsTrigger>
          <TabsTrigger value="email" data-testid="ptab-email"><Mail className="w-3.5 h-3.5 mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="history" data-testid="ptab-history"><History className="w-3.5 h-3.5 mr-1.5" />Login History</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-5">
          <Card>
            <div className="space-y-4">
              <Field label="Full Name"><Input value={profileForm.full_name || ""}
                onChange={(e) => setProfileForm((f) => ({ ...f, full_name: e.target.value }))}
                data-testid="profile-fullname" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mobile"><Input value={profileForm.mobile || ""}
                  onChange={(e) => setProfileForm((f) => ({ ...f, mobile: e.target.value }))}
                  data-testid="profile-mobile" /></Field>
                <Field label="Designation"><Input value={profileForm.designation || ""}
                  onChange={(e) => setProfileForm((f) => ({ ...f, designation: e.target.value }))}
                  data-testid="profile-designation" /></Field>
              </div>
              <Field label="Profile Photo">

  		{profileForm.photo_url && (
    		<img
  src={
    profileForm.photo_url?.startsWith("http")
      ? profileForm.photo_url
      : `${import.meta.env.VITE_API_URL}${profileForm.photo_url}`
  }
  alt=""
  className="w-24 h-24 rounded-full"
/>
                 )}

  		<input
   		 type="file"
   		 accept="image/*"
   		 onChange={(e) => {
    		  if (e.target.files?.[0]) {
     		   uploadPhoto(e.target.files[0]);
     		 }
    		}}
 		 />

		</Field>
              <Field label="Email (read-only — use Change Email tab)">
                <Input value={profileForm.email || ""} disabled />
              </Field>
              <div className="flex justify-end pt-2 border-t border-border">
                <PrimaryButton onClick={saveProfile} testId="save-profile-btn">
                  <Save className="w-4 h-4 mr-1.5" /> Save profile
                </PrimaryButton>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="password" className="mt-5">
          <Card>
            <div className="space-y-4 max-w-md">
              <Field label="Current Password"><Input type="password"
                value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                data-testid="current-password-input" /></Field>
              <Field label="New Password"><Input type="password"
                value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                data-testid="new-password-input" /></Field>
              <Field label="Confirm New Password"><Input type="password"
                value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                data-testid="confirm-password-input" /></Field>
              <div className="flex justify-end pt-2 border-t border-border">
                <PrimaryButton onClick={savePwd} testId="change-password-btn"
                  disabled={!pwd.current_password || !pwd.new_password}>
                  Change password
                </PrimaryButton>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="mt-5">
          <Card>
            <div className="space-y-4 max-w-md">
              <Field label="Current Email"><Input value={user.email || "—"} disabled /></Field>
              <Field label="New Email"><Input type="email"
                value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                data-testid="new-email-input" /></Field>
              <Field label="Password Confirmation"><Input type="password"
                value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                data-testid="confirm-email-password" /></Field>
              <div className="flex justify-end pt-2 border-t border-border">
                <PrimaryButton onClick={saveEmail} testId="change-email-btn"
                  disabled={!emailForm.new_email || !emailForm.password}>
                  Update email
                </PrimaryButton>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <Card>
            {history.length === 0 ? <Empty label="No login history yet." /> : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr><Th>Login</Th><Th>Logout</Th><Th>Duration</Th><Th>IP</Th><Th>Device / Browser</Th></tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-border" data-testid={`history-${h.id}`}>
                      <Td className="text-xs mono">{h.login_at ? new Date(h.login_at).toLocaleString() : "—"}</Td>
                      <Td className="text-xs mono">{h.logout_at ? new Date(h.logout_at).toLocaleString() : "—"}</Td>
                      <Td className="text-xs mono">{h.duration_seconds ? `${Math.round(h.duration_seconds / 60)}m` : "—"}</Td>
                      <Td className="text-xs mono">{h.ip || "—"}</Td>
                      <Td className="text-xs text-muted-foreground truncate max-w-xs">{h.user_agent || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children }) {
  return <div className="border border-border rounded-sm bg-background p-6">{children}</div>;
}
function Empty({ label }) { return <div className="text-sm text-muted-foreground text-center py-6">{label}</div>; }
function Th({ children }) { return <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-2 ${className}`}>{children}</td>; }
function PrimaryButton({ children, onClick, testId, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50 flex items-center"
      style={{ backgroundColor: "var(--theme-color)" }}>
      {children}
    </button>
  );
}
